/**
 * ionRailHeartbeat — pure decision logic for the collapsed rail's
 * "heartbeat" (S3 of the onboarding rework, .planning/ion-onboarding-s3-s4-
 * design.md). Kept DOM/signal-free so the two decisions are unit-testable
 * without mounting IonSurface — mirrors toolStatusLabels.js /
 * IonWorkingCue's formatElapsed.
 *
 * Two independent questions the rail asks on every render:
 *   1. railActivityStatus — is Ion actively streaming, and if so is it
 *      "steering" (just drove the page via an explorer-control tool) or
 *      generically "working" (composing text, or the steering cue has
 *      decayed)?
 *   2. railIdleContext — when NOT active, what compact context hint (if
 *      any) should the rail's tooltip surface for the current page?
 */

// A commandProvenance write with source:'ion' counts as "Ion just steered
// the page" for this long after it lands — long enough to bridge the gap
// between two tool calls in the same turn, short enough that the rail falls
// back to the generic "working…" once Ion goes quiet (e.g. composing a text
// reply) instead of claiming to still be steering forever.
export const STEERING_WINDOW_MS = 4000

// Only these commandLayer.js commands represent Ion actually STEERING the
// page (filter/view/context — see the setProvenance('filter'|'view'|
// 'context', ...) call sites). `commit` and `revert` are turn-end
// bookkeeping that commandLayer.js writes with source:'ion' too (committing
// the working set at the end of every controlling turn, or reverting one on
// failure) — NOT a fresh act of steering. Without this whitelist, a
// same-turn `commit` write (fresh `at`) would make the rail claim
// "steering…" for the next 4s even while Ion is only composing a plain-text
// reply to an unrelated follow-up — exactly the false-positive this rail
// state exists to avoid. Exported so tests can lock it against the real
// values written in commandLayer.js instead of duplicating them blind.
// Commands that mean "Ion is driving the UI" rather than "Ion is thinking".
// A command missing here makes the rail say "working" while Ion visibly steers —
// wrong, and invisible in a test that only checks the happy path.
export const STEERING_COMMANDS = ['filter', 'view', 'context', 'plot', 'columns']

/**
 * @param {boolean} streaming — chatStreaming.value
 * @param {{command: string, source: string, at: number}|null} provenance — commandProvenance.value
 * @param {number} now — Date.now(), passed in so this stays pure/testable
 * @returns {'steering'|'working'|null} null means idle (not streaming)
 */
export function railActivityStatus(streaming, provenance, now) {
  if (!streaming) return null
  if (!provenance || provenance.source !== 'ion') return 'working'
  if (!STEERING_COMMANDS.includes(provenance.command)) return 'working'
  const age = now - provenance.at
  // Guard against negative age (clock skew / NTP step-back) — without the
  // `>= 0` a backward jump would make `age < STEERING_WINDOW_MS` true
  // forever, pinning "steering…" for the rest of the stream.
  const justSteered = age >= 0 && age < STEERING_WINDOW_MS
  return justSteered ? 'steering' : 'working'
}

/**
 * Is this a route the rail's idle context hint is defined for? Kept as the
 * single source of truth for "which pathnames" so the component can gate a
 * signal read (filteredCells/allCells) on it BEFORE reading the signal —
 * reading a signal only inside a conditional means it's only subscribed to
 * when that branch runs, so a route that never needs the hint never
 * subscribes to it.
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export function isRailContextRoute(pathname) {
  return pathname === '/explorer' || pathname === '/cells'
}

/**
 * Idle context hint for the collapsed rail. Only defined for the Explorer —
 * the one surface this design targets — every other route returns null (the
 * rail keeps its plain "Ask Ion" wordmark/aria-label, no tooltip).
 *
 * @param {string} pathname
 * @param {number} filteredCount
 * @param {boolean} catalogueLoaded — has the Explorer's cell catalogue
 *   (globalSignals.js's `allCells`) finished its initial load? Before that,
 *   `filteredCells` is still its `[]` initial value, and surfacing "0
 *   matches" would misreport "still loading" as "nothing matched".
 * @returns {{key: string, params: {count: number}}|null}
 */
export function railIdleContext(pathname, filteredCount, catalogueLoaded) {
  if (!isRailContextRoute(pathname)) return null
  if (!catalogueLoaded) return null
  if (!Number.isFinite(filteredCount)) return null
  const key =
    filteredCount === 1 ? 'ion.rail.idle.explorer.one' : 'ion.rail.idle.explorer.many'
  return { key, params: { count: filteredCount } }
}
